import React from 'react';
import { withTranslation } from 'react-i18next';
import { Row, Col } from 'react-bootstrap';
import { ProductService } from '../../services';
import { DefaultButton, LoadingButton } from '../buttons';
import { TextField, Checkbox } from '../fields';
import { Toast } from '../alerts';
import { ModalLayout } from '../layouts';

import '../../styles/modals/modal.css';

/** Internal only: upload a menu, review the extracted products, then create them through the normal product endpoint. */
class ImportMenuModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = this.initialState();
    this.productService = new ProductService();
    this.fileInput = React.createRef();
  }

  initialState() {
    return { show: false, venue: null, file: null, loading: false, saving: false, progress: '', error: null, rows: [], created: [], imaging: false, done: 0 };
  }

  componentDidMount() {
    if (this.props.onRef) this.props.onRef(this);
  }

  componentWillUnmount() {
    if (this.props.onRef) this.props.onRef(undefined);
  }

  show(show, venue = null) {
    this.setState({ ...this.initialState(), show, venue });
  }

  errorFrom(e, fallback) {
    return (e && e.errors && e.errors[0]) || (e && e.message) || fallback;
  }

  extract = async () => {
    const { venue, file } = this.state;
    if (!venue || !file) {
      this.setState({ error: 'Choose a menu file first' });
      return;
    }

    this.setState({ loading: true, error: null, rows: [] });
    try {
      const result = await this.productService.importMenu(venue._id, file);
      if (result && result.success) {
        this.setState({ loading: false, rows: result.products.map((p, i) => ({ ...p, key: i, include: true })) });
      } else {
        this.setState({ loading: false, error: this.errorFrom(result, 'Extraction failed') });
      }
    } catch (e) {
      this.setState({ loading: false, error: this.errorFrom(e, 'Extraction failed') });
    }
  };

  updateRow(key, patch) {
    this.setState({ rows: this.state.rows.map((r) => (r.key === key ? { ...r, ...patch } : r)) });
  }

  updatePrice(key, index, patch) {
    this.setState({
      rows: this.state.rows.map((r) => (r.key === key ? { ...r, prices: r.prices.map((p, i) => (i === index ? { ...p, ...patch } : p)) } : r)),
    });
  }

  save = async () => {
    const { venue, rows } = this.state;
    const selected = rows.filter((r) => r.include && r.name.trim());
    if (!selected.length) return;

    this.setState({ saving: true, error: null, progress: 'Creating categories' });
    try {
      const names = [...new Set(selected.map((r) => (r.category || '').trim()).filter(Boolean))];
      const categoryResult = await this.productService.importMenuCategories(venue._id, names);
      if (!categoryResult || !categoryResult.success) throw new Error('Could not create categories');

      const idByName = new Map(categoryResult.categories.map((c) => [c.name.trim().toLowerCase(), c._id]));
      const failedKeys = [];
      const created = [];

      for (let i = 0; i < selected.length; i++) {
        const r = selected[i];
        this.setState({ progress: `Creating ${i + 1} of ${selected.length}: ${r.name}` });

        const prices = r.prices
          .map((p) => ({ name: (p.name || '').trim() || 'Regular', price: Number(p.price), ingredients: [] }))
          .filter((p) => Number.isFinite(p.price) && p.price >= 0);

        if (!prices.length) {
          failedKeys.push(r.key);
          continue;
        }

        try {
          const categoryId = idByName.get((r.category || '').trim().toLowerCase()) || null;
          const res = await this.productService.post(venue._id, r.name.trim(), (r.note || '').trim(), null, prices, [], true, categoryId, r.is_food, false, '');
          if (!res || !res.success) {
            failedKeys.push(r.key);
          } else if (res.product) {
            created.push({ _id: res.product._id, name: res.product.name, status: 'pending' });
          }
        } catch (e) {
          failedKeys.push(r.key);
        }
      }

      if (this.props.onComplete) this.props.onComplete();

      const createdCount = selected.length - failedKeys.length;
      if (failedKeys.length) {
        this.setState({
          saving: false,
          progress: '',
          error: `${failedKeys.length} item(s) failed to save and are still listed below.`,
          rows: rows.filter((r) => failedKeys.includes(r.key)),
        });
        if (createdCount) Toast.success(`${createdCount} products created`);
      } else {
        Toast.success(`${createdCount} products created`);
        this.setState({ saving: false, progress: '', rows: [], created: created });
      }
    } catch (e) {
      this.setState({ saving: false, progress: '', error: this.errorFrom(e, 'Save failed') });
    }
  };

  generateImages = async () => {
    const { created } = this.state;
    /** Instance flag, not state: setState is async, so a state read here is still false on the first pass. */
    this.stopImaging = false;
    this.setState({ imaging: true, error: null, done: 0 });

    for (let i = 0; i < created.length; i++) {
      if (this.stopImaging) break;

      this.setState({ progress: `Creating image ${i + 1} of ${created.length}: ${created[i].name}` });
      let status = 'failed';
      try {
        const result = await this.productService.generateImage(created[i]._id);
        if (result && result.success) status = 'done';
        else if (result && result.errors) this.setState({ error: result.errors[0] });
      } catch (e) {
        this.setState({ error: this.errorFrom(e, 'Image generation failed') });
      }

      this.setState({
        created: this.state.created.map((p, index) => (index === i ? { ...p, status } : p)),
        done: this.state.done + 1,
      });
    }

    if (this.props.onComplete) this.props.onComplete();
    this.setState({ imaging: false, progress: '' });
  };

  render() {
    const { t } = this.props;
    const { show, venue, file, loading, saving, progress, error, rows, created, imaging, done } = this.state;
    const included = rows.filter((r) => r.include).length;
    const failedImages = created.filter((p) => p.status === 'failed').length;

    return (
      show && (
        <ModalLayout title={t('Import menu')} subtitle={venue ? venue.name : null} width={rows.length ? 980 : created.length ? 640 : 540}>
          <input
            ref={this.fileInput}
            type='file'
            accept='.pdf,image/png,image/jpeg,image/webp'
            style={{ display: 'none' }}
            onChange={(e) => this.setState({ file: e.target.files && e.target.files[0] ? e.target.files[0] : null, error: null })}
          />
          <div className='modal-buttons-container' style={{ marginTop: 10 }}>
            <div className='modal-button'>
              <DefaultButton text={file ? file.name : t('CHOOSE FILE')} onClick={() => this.fileInput.current && this.fileInput.current.click()} fullWidth />
            </div>
            <div className='modal-button'>
              <LoadingButton text={t('EXTRACT MENU')} state={loading ? 'LOADING' : 'DONE'} onClick={this.extract} fullWidth />
            </div>
          </div>

          {loading && <p style={{ marginTop: 14, textAlign: 'center' }}>{t('Reading the menu. Large menus can take up to a minute.')}</p>}
          {error && (
            <span className='modal-error-text' style={{ marginTop: 14 }}>
              {error}
            </span>
          )}

          {created.length > 0 && (
            <>
              <p style={{ marginTop: 20 }}>
                <b>{created.length} products created.</b>{' '}
                {t('Images are optional. They are generated from each name and description, so treat them as placeholders until you have real photos.')}
              </p>
              {created.map((p) => (
                <Row key={p._id}>
                  <Col md={8}>{p.name}</Col>
                  <Col md={4} style={{ textAlign: 'right' }}>
                    {p.status === 'done' ? t('Image added') : p.status === 'failed' ? t('No image') : t('Waiting')}
                  </Col>
                </Row>
              ))}
              {done > 0 && !imaging && (
                <p style={{ marginTop: 12 }}>
                  <b>
                    {done - failedImages} {t('images added')}
                    {failedImages ? `, ${failedImages} ${t('failed')}` : ''}
                  </b>
                </p>
              )}
            </>
          )}

          {rows.length > 0 && (
            <>
              <p style={{ marginTop: 24 }}>
                <b>
                  {included} of {rows.length} selected.
                </b>{' '}
                {t('Check names and prices before creating. Photos and extras can be added afterwards in the product editor.')}
              </p>

              {rows.map((r) => (
                <div key={r.key} style={{ opacity: r.include ? 1 : 0.4, borderTop: '1px solid #eee', paddingTop: 12, marginTop: 8 }}>
                  <Row>
                    <Col md={1} style={{ paddingTop: 14 }}>
                      <Checkbox text='' value={r.key} checked={r.include} onChange={(checked) => this.updateRow(r.key, { include: checked })} />
                    </Col>
                    <Col md={5}>
                      <TextField fullWidth label={t('Name')} value={r.name} onChange={(e) => this.updateRow(r.key, { name: e.target.value })} />
                    </Col>
                    <Col md={4}>
                      <TextField fullWidth label={t('Category')} value={r.category} onChange={(e) => this.updateRow(r.key, { category: e.target.value })} />
                    </Col>
                    <Col md={2} style={{ paddingTop: 14 }}>
                      <Checkbox text={t('Food')} value={r.key} checked={r.is_food} onChange={(checked) => this.updateRow(r.key, { is_food: checked })} />
                    </Col>
                  </Row>
                  <Row>
                    <Col md={{ span: 11, offset: 1 }}>
                      <Row>
                        {r.prices.map((p, i) => (
                          <Col md={4} key={i}>
                            <Row>
                              <Col md={7}>
                                <TextField fullWidth label={t('Option')} value={p.name} onChange={(e) => this.updatePrice(r.key, i, { name: e.target.value })} />
                              </Col>
                              <Col md={5}>
                                <TextField fullWidth label={t('Price')} value={String(p.price)} onChange={(e) => this.updatePrice(r.key, i, { price: e.target.value })} />
                              </Col>
                            </Row>
                          </Col>
                        ))}
                      </Row>
                    </Col>
                    <Col md={{ span: 11, offset: 1 }}>
                      <TextField fullWidth label={t('Note')} value={r.note} onChange={(e) => this.updateRow(r.key, { note: e.target.value })} />
                    </Col>
                  </Row>
                </div>
              ))}
            </>
          )}

          {progress && <p style={{ marginTop: 14, textAlign: 'center' }}>{progress}</p>}

          <div className='modal-buttons-container'>
            <div className={rows.length ? 'modal-button' : 'modal-button-full'}>
              {saving || imaging || <DefaultButton text={created.length ? t('CLOSE') : t('CANCEL')} onClick={() => this.show(false)} fullWidth />}
            </div>
            {rows.length > 0 && (
              <div className='modal-button'>
                <LoadingButton text={`${t('CREATE')} ${included} ${t('PRODUCTS')}`} state={saving ? 'LOADING' : 'DONE'} onClick={this.save} fullWidth />
              </div>
            )}
            {created.length > 0 && (
              <div className='modal-button'>
                {imaging ? (
                  <DefaultButton text={t('STOP')} onClick={() => { this.stopImaging = true; this.setState({ imaging: false }); }} fullWidth />
                ) : (
                  <LoadingButton text={`${t('GENERATE')} ${created.length} ${t('IMAGES')}`} state='DONE' onClick={this.generateImages} fullWidth />
                )}
              </div>
            )}
          </div>
        </ModalLayout>
      )
    );
  }
}

export default withTranslation()(ImportMenuModal);
